import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  CSSProperties,
  ReactNode,
  useCallback,
  Ref,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import _uniq from 'lodash/uniq';
import { DebouncedFunc } from 'lodash';

import { useTheme } from '@cobalt/react-theme-provider';
import useOutsideClick from '@cobalt/react-outside-click-hook';
import Dropdown, { Content } from '@cobalt/react-dropdown';
import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import List, { ClickableItem } from '@cobalt/react-list';
import Spinner from '@cobalt/react-spinner';
import { Text } from '@cobalt/react-typography';
import Icon from '@cobalt/react-icon';
import Divider from '@cobalt/react-divider';

import Selector from './Selector';
import SelectSearchInput from './SelectSearchInput';
import Listor from './Listor';

import useOptions from './hooks/useOptions';
import useShownOptions from './hooks/useShownOptions';
import useCache from './hooks/useCache';

import { mergeOptions } from './util';
import { SelectOptionType } from './types';

const cls = 'select';

export const MIN_SEARCH_CHARS = 3;

const SEARCH_INPUT_HEIGHT = 40;
const LISTOR_MAX_HEIGHT = 200;
const SEARCH_DELAY = 300;

enum SearchStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  MIN_CHARS = 'MIN_CHARS',
  NO_RESULT = 'NO_RESULT',
  ERROR = 'ERROR',
}

export type SelectRef = {
  handleChangeSearchValue: (value: string) => void;
};

export interface SelectProps<T extends SelectOptionType = SelectOptionType> {
  value: string | string[];
  options?: T[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  multiple?: boolean;
  showSelectAll?: boolean;
  loading?: boolean;
  showSearch?: boolean;
  isRemoteSearch?: boolean;
  minSearchChars?: number;
  filterOption?: boolean | ((inputValue: string, option: T) => boolean);
  moreOptionsTip?: string;
  transparent?: boolean;
  borderless?: boolean;
  size?: 'small' | 'medium';
  variation?: 'error' | 'warning';
  maxTagCount?: number;
  maxTagWidth?: CSSProperties['maxWidth'];
  className?: string;
  style?: CSSProperties;
  dataPrefix?: string;
  inputStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  prefixCls?: string;
  forwardedRef?: Ref<SelectRef>;
  searchInputPlaceholderText?: string;
  loadingText?: string;
  selectAllText?: string;
  atLeastCountCharsText?: string;
  noResultsFoundText?: string;
  searchErrorText?: string;
  tryAgainText?: string;
  children?: ReactNode;
  actionElement?: ReactNode;
  onChange: (value: string | string[], option: T[] | undefined) => void;
  onClear?: () => void;
  onSearch?: (value: string) => void | Promise<T[] | null>;
  onBlur?: () => void;
  onSelect?: (value: string, option?: T) => void;
  onDeselect?: (value: string, option?: T) => void;
  onToggle?: (open: boolean) => void;
}

function Select<T extends SelectOptionType>(props: SelectProps<T>) {
  const [t] = useTranslation();

  const {
    value,
    options,
    placeholder = t('common.select_one', { defaultValue: 'Select one' }),
    disabled = false,
    multiple = false,
    showSelectAll = false,
    loading = false,
    showSearch = false,
    isRemoteSearch = false,
    minSearchChars = MIN_SEARCH_CHARS,
    filterOption = true,
    moreOptionsTip,
    borderless = false,
    size,
    className,
    style,
    contentStyle,
    prefixCls = 'cobalt',
    dataPrefix,
    children,
    forwardedRef,
    actionElement,
    searchInputPlaceholderText = t('component.select.search', { defaultValue: 'Search' }),
    loadingText = t('component.select.loading_ellipsis', { defaultValue: 'Loading...' }),
    selectAllText = t('component.select.select_all', { defaultValue: 'Select all' }),
    atLeastCountCharsText = t('component.select.at_least_count_characters', {
      count: minSearchChars,
      defaultValue: `At least ${minSearchChars === 1 ? '1 character' : `${minSearchChars} characters`}`,
    }),
    noResultsFoundText = t('common.no_results_found', { defaultValue: 'No results found' }),
    searchErrorText = t('component.select.an_error_occurred_please_try_again', {
      defaultValue: 'An error occurred. Please try again',
    }),
    tryAgainText = t('component.select.try_again_ellipsis', { defaultValue: 'Try again...' }),
    onChange,
    onClear,
    onSearch,
    onBlur,
    onSelect,
    onDeselect,
    onToggle,
  } = props;

  const componentCls = `${prefixCls}-${cls}`;
  const mergeClassNames = classNames(`${componentCls}`, className);

  const theme = useTheme();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const debounceHandlerSearchRef = useRef<DebouncedFunc<(value: string) => void>>();

  useImperativeHandle(forwardedRef, () => ({
    handleChangeSearchValue,
  }));

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchStatus, setSearchStatus] = useState(SearchStatusEnum.NORMAL);
  const [selectedValue, setSelectedValue] = useState<string | string[]>(multiple ? [] : '');

  const parsedOptions = useOptions<T>(options, children);
  const shownOptions = useShownOptions<T>(filterOption, parsedOptions, searchValue, minSearchChars);
  const { getCacheOptions } = useCache<T>(value, parsedOptions);

  const genDirection = () => {
    const { clientHeight } = document.body;
    const bottom = dropdownRef.current?.getBoundingClientRect().bottom || 0;
    const offsetBottom = clientHeight - bottom;
    const contentHeight = (showSearch ? SEARCH_INPUT_HEIGHT : 0) + LISTOR_MAX_HEIGHT;
    return offsetBottom > contentHeight ? 'down' : 'up';
  };

  const checkMinSearchChars = useCallback(
    (value: string) => {
      const len = value.length;
      return len === 0 || len >= minSearchChars;
    },
    [minSearchChars],
  );

  const showMinChars = useMemo(() => {
    return isRemoteSearch ? searchStatus === SearchStatusEnum.MIN_CHARS : !checkMinSearchChars(searchValue);
  }, [isRemoteSearch, searchStatus, checkMinSearchChars, searchValue]);

  const showNoResultsFound = useMemo(() => {
    return isRemoteSearch
      ? searchStatus === SearchStatusEnum.NO_RESULT
      : shownOptions.length === 0 && checkMinSearchChars(searchValue);
  }, [isRemoteSearch, searchStatus, shownOptions.length, checkMinSearchChars, searchValue]);

  const showSearchError = useMemo(() => {
    return searchStatus === SearchStatusEnum.ERROR;
  }, [searchStatus]);

  const showListor = useMemo(() => {
    return !showMinChars && !showNoResultsFound && !showSearchError && shownOptions.length > 0;
  }, [showMinChars, showNoResultsFound, showSearchError, shownOptions.length]);

  const outsideClickHandler = useCallback(() => {
    if (!isOpen) return;
    setIsOpen(false);
    onBlur?.();
    onToggle?.(false);
  }, [isOpen, onBlur, onToggle]);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const handleToggleOpen = useCallback(
    (_open?: boolean) => {
      if (disabled || loading || _open === isOpen) return;
      onClickCapture();

      const open = _open === undefined ? !isOpen : _open;
      setIsOpen(open);
      if (!open) onBlur?.();
      onToggle?.(open);
    },
    [disabled, isOpen, loading, onBlur, onClickCapture, onToggle],
  );

  const handleClearSelectedValue = useCallback(() => {
    const value = multiple ? [] : '';
    const option = multiple ? [] : undefined;

    setSelectedValue(value);
    onChange(value, option);
    onClear?.();
  }, [multiple, onChange, onClear]);

  const handlerSearch = useCallback(
    async (value: string) => {
      const promise = onSearch?.(value);

      const validMinSearchChars = checkMinSearchChars(value);

      if (isRemoteSearch && promise instanceof Promise) {
        if (!validMinSearchChars) {
          setSearchStatus(SearchStatusEnum.MIN_CHARS);
        } else {
          try {
            setSearchStatus(SearchStatusEnum.LOADING);
            const result = await promise;

            if (result === null) {
              return;
            } else if (result.length === 0 && value.length >= MIN_SEARCH_CHARS) {
              setSearchStatus(SearchStatusEnum.NO_RESULT);
            } else {
              setSearchStatus(SearchStatusEnum.NORMAL);
            }
          } catch (err) {
            setSearchStatus(SearchStatusEnum.ERROR);
          }
        }
      } else if (!validMinSearchChars) {
        setSearchStatus(SearchStatusEnum.NORMAL);
        isRemoteSearch && debounceHandlerSearchRef.current?.cancel();
      }
    },
    [checkMinSearchChars, isRemoteSearch, onSearch],
  );

  const handleChangeSearchValue = useCallback(
    (value: string) => {
      setSearchValue(value);
      debounceHandlerSearchRef.current?.cancel();

      if (value.length !== 0 && value.length >= minSearchChars && isRemoteSearch) {
        debounceHandlerSearchRef.current?.(value);
      } else {
        handlerSearch(value);
      }
    },
    [handlerSearch, isRemoteSearch, minSearchChars],
  );

  const handleChangeSelect = useCallback(
    (value: string) => {
      if (value === undefined) return;

      const allOptions = mergeOptions(getCacheOptions(), shownOptions);

      if (Array.isArray(selectedValue)) {
        const newValues = [...selectedValue];
        const index = selectedValue.indexOf(value);
        const hasChecked = index !== -1;
        const option = allOptions.find((option) => option.value === value);

        if (!hasChecked) {
          newValues.push(value);
          onSelect?.(value, option);
        } else {
          newValues.splice(index, 1);
          onDeselect?.(value, option);
        }

        const newOptions = newValues.map((tempValue) => {
          return allOptions.find((item) => item.value === tempValue) || ({} as T);
        });
        onChange(newValues, newOptions);
      } else {
        const option = allOptions.find((item) => item.value === value);
        onSelect?.(value, option);

        if (value !== selectedValue) {
          onChange(value, option ? [option] : undefined);
        }

        setIsOpen(false);
        onBlur?.();
        onToggle?.(false);
      }
    },
    [getCacheOptions, onBlur, onChange, onDeselect, onSelect, onToggle, selectedValue, shownOptions],
  );

  const handleChangeSelectAll = useCallback(
    (selectAll: boolean) => {
      const tempValue = Array.isArray(value) ? value : [];

      const enabledOptions = shownOptions.filter((option) => !option.disabled);
      const enabledValues = enabledOptions.map((option) => option.value);
      const allOptions = mergeOptions(getCacheOptions(), enabledOptions);

      const newValues = selectAll
        ? _uniq(tempValue.concat(enabledValues))
        : tempValue.filter((v) => !enabledValues.includes(v));
      const newOptions = allOptions.filter((option) => newValues.includes(option.value));

      setSelectedValue(newValues);
      onChange(newValues, newOptions);
    },
    [getCacheOptions, onChange, shownOptions, value],
  );

  const handleTryAgain = useCallback(() => {
    handlerSearch(searchValue);
  }, [handlerSearch, searchValue]);

  useEffect(() => {
    setSelectedValue(value || (multiple ? [] : ''));
  }, [multiple, value]);

  useEffect(() => {
    if (!isRemoteSearch) return;
    debounceHandlerSearchRef.current = _debounce(handlerSearch, SEARCH_DELAY);
  }, [handlerSearch, isRemoteSearch]);

  return (
    <Dropdown
      data-bi={dataPrefix}
      style={style}
      forwardedRef={dropdownRef}
      open={isOpen}
      borderless={borderless}
      direction={genDirection()}
    >
      <Selector
        {...props}
        options={shownOptions}
        placeholder={placeholder}
        isOpen={isOpen}
        inputRef={inputRef}
        selectedValue={selectedValue}
        size={size}
        className={mergeClassNames}
        loadingText={loadingText}
        onToggleOpen={handleToggleOpen}
        onClearSelect={handleChangeSelect}
        onClearSelectedValue={handleClearSelectedValue}
      />

      <Content style={contentStyle} data-testid="content" inputRef={inputRef.current} onClick={onClickCapture}>
        <List>
          {/* Search input */}
          {showSearch && (
            <SelectSearchInput
              searchValue={searchValue}
              placeholder={searchInputPlaceholderText}
              onChangeSearchValue={handleChangeSearchValue}
            />
          )}

          {actionElement && (
            <>
              <Box padding="2">{actionElement}</Box>

              <Divider />
            </>
          )}

          {/* Loading... */}
          {searchStatus === SearchStatusEnum.LOADING && (
            <Flex alignX="center" alignY="center" paddingY="4" gap="2">
              <Spinner size="tiny" />
              <Text>{loadingText}</Text>
            </Flex>
          )}

          {searchStatus !== SearchStatusEnum.LOADING && (
            <>
              {/* Listor */}
              {showListor && (
                <Listor
                  style={{ maxHeight: LISTOR_MAX_HEIGHT }}
                  selectedValue={selectedValue}
                  options={shownOptions}
                  multiple={multiple}
                  moreOptionsTip={moreOptionsTip}
                  selectAllText={selectAllText}
                  onChangeSelect={handleChangeSelect}
                  onChangeSelectAll={showSelectAll ? handleChangeSelectAll : undefined}
                />
              )}

              {/* Min search characters */}
              {showMinChars && (
                <Box padding="4">
                  <Text color={theme.gray500}>{atLeastCountCharsText}</Text>
                </Box>
              )}

              {/* No results found */}
              {showNoResultsFound && (
                <Box padding="4">
                  <Text color={theme.gray500}>{noResultsFoundText}</Text>
                </Box>
              )}

              {/* Search error */}
              {showSearchError && (
                <Box padding="2">
                  <ClickableItem onClick={handleTryAgain}>
                    <Flex direction="column" alignX="center" padding="2">
                      <Flex paddingBottom="2" gap="1">
                        <Icon name="error_outline" size="micro" color={theme.error700} />
                        <Text size="small" color={theme.error700}>
                          {searchErrorText}
                        </Text>
                      </Flex>

                      <Text size="medium" weight="medium" color={theme.primary700}>
                        {tryAgainText}
                      </Text>
                    </Flex>
                  </ClickableItem>
                </Box>
              )}
            </>
          )}
        </List>
      </Content>
    </Dropdown>
  );
}

Select.defaultProps = {
  allowClear: false,
  maxTagCount: 2,
};

export default Select;
